import { env } from '../../../config/env';
import type { AuthResponse } from './mockAuthService';
import type { CognitoAuthResponse } from './cognitoService';

// Export the common response type
export type { AuthResponse };

// Create a unified auth service interface
export interface IAuthService {
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signOut: () => void;
  getCurrentSession: () => Promise<AuthResponse | null>;
}

// Helper function to adapt Cognito response to our common AuthResponse type
function adaptCognitoResponse(cognitoResponse: CognitoAuthResponse): AuthResponse {
  return {
    token: cognitoResponse.token,
    user: {
      email: cognitoResponse.user.email,
      sub: cognitoResponse.user.sub,
      groups: cognitoResponse.user.groups
    }
  };
}

// Create the mock auth service implementation
async function createMockAuthService(): Promise<IAuthService> {
  const { mockAuthService } = await import('./mockAuthService');
  return mockAuthService;
}

// Create the Cognito auth service implementation
async function createCognitoAuthService(): Promise<IAuthService> {
  const { cognitoService } = await import('./cognitoService');
  return {
    signIn: async (email, password) => {
      const response = await cognitoService.signIn(email, password);
      return adaptCognitoResponse(response);
    },
    signOut: cognitoService.signOut,
    getCurrentSession: async () => {
      const session = await cognitoService.getCurrentSession();
      return session ? adaptCognitoResponse(session) : null;
    }
  };
}

// Factory function to get the appropriate auth service
export async function getAuthService(): Promise<IAuthService> {
  return env.AUTH_PROVIDER === 'cognito' 
    ? await createCognitoAuthService()
    : await createMockAuthService();
} 